import React from 'react'
import { useHistory } from 'react-router'
import {
  Calendar1,
  Calendar2,
  Call,
  ClipboardTick,
  Eraser,
  ProfileDelete,
  Setting3,
  Sms,
  Tag,
  TickCircle,
  UserOctagon,
  UserTag
} from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import AgentPage from '../../components/templates/AgentPage'
import Table from '../../components/molecules/Table'
import Loading from '../../components/molecules/Table/Loading'
import Header from '../../components/molecules/Table/Header'
import SelectableClient from '../../components/molecules/Table/Cells/SelectableClient'
import ClientStatus from '../../components/molecules/Table/Cells/ClientStatus'
import Channel from '../../components/molecules/Table/Cells/Channel'
import ClientTasks from '../../components/molecules/Table/Cells/ClientTasks'
import ErrorToast from '../../components/molecules/ErrorToast'
import Filter from './Filters'
import ActionButton from '../../components/organisms/ActionButton'
import UserType from '../../components/molecules/Table/Cells/UserType'
import CreatedBy from '../../components/molecules/Table/Cells/CreatedBy'
import FieldFilters from './FieldFilters'
import FieldPills, { SimpleValue } from './FieldFilters/FieldPills'
import TableSettingsModal from './TableSettingsModal'
import CustomField from '../../components/molecules/Table/Cells/CustomFieldComponent'
import DateCell from '@SonarCX/components/molecules/Table/Cells/Date'
import TagCell from '@SonarCX/components/molecules/Table/Cells/Tag'

import { filterPermissions, taskSorter } from './utils'
import { Routes } from '@SonarCX/Routes'
import { isUUID } from '@SonarCX/utilities'
import {
  CustomFieldSearch,
  CUSTOMER_TABLE_ORDER_SETTINGS_KEY,
  CUSTOMER_TABLE_VISIBILITY_SETTINGS_KEY,
  ALL_COLUMNS,
  FIELD_LIMIT,
  FIELD_INCLUDE_LIMIT,
  fetchPageOfUsers,
  resetSelection,
  selectContactsTable,
  setCurrentUser,
  setPaginationPage,
  setPaginationSize,
  setSort,
  setOrder,
  setVisible,
  resetPagination
} from '../../slices/contactsTable'

import useDelayedUserSearch, {
  DEFAULT_FILTER,
  MIN_CHAR_COUNT
} from '../../hooks/useDelayedUserSearch'
import useUserPermissions from '../../hooks/permissions/useUserPermissions'
import useAccess from '../../hooks/useAccess'
import { useUserAPI, DEFAULT_EMBEDS } from '../../providers/UserAPIProvider'
import { useEnvironment } from '../../providers/EnvironmentProvider'
import { useBreakpoint } from '../../providers/BreakpointProvider'
import { fetchUserStatus, selectUserStatus } from '../../slices/userStatus'
import { useAreYouSure } from '../../providers/AreYouSureProvider'
import { deleteUsers } from '../../lib/api'
import { AddErrorAlert, AddSuccessAlert } from '../../slices/alert'
import { selectCustomFields } from '../../slices/customFields'
import { parseSort } from '@SonarCX/slices/contactsTable/helpers'
import useSavedValue from '../../hooks/useSavedValue'
import useStyles from './styles'

const columnHelper = createColumnHelper<WithMeta<LocalUser>>()
export const CUSTOM_FIELDS_DROPDOWN_STORAGE_KEY = 'custom_field_dropdown'
export const CUSTOMER_SEARCH_STORAGE_KEY = 'customer_search'
export const CUSTOM_FIELDS_VALUE_STORAGE_KEY = 'custom_field_values'
const TABLE_ID = 'contacts-table'

const Customers = () => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const hasPermission = useAccess()
  const {
    pagination,
    sort,
    tableApiSort,
    filters,
    paginationTotal,
    currentUser: filterCurrentUser,
    showSelector,
    selected: selectedUsers,
    order,
    visible
  } = useAppSelector(selectContactsTable)
  const { hasAdmin } = useUserPermissions()
  const { updateUsers } = useUserAPI()
  const { loaded: statusesLoaded, loading } = useAppSelector(selectUserStatus)
  const {
    search: handleSearch,
    results,
    error,
    loading: searchLoading
  } = useDelayedUserSearch(true, DEFAULT_FILTER, DEFAULT_EMBEDS)
  const { user: currentUser } = useEnvironment()
  const { isSmall, isMedium } = useBreakpoint()
  const { customFields } = useAppSelector(selectCustomFields)

  const [applySearchFilter, setApplySearchFilter] = React.useState(false)
  const [loadingPages, setLoadingPages] = React.useState(false)
  const [initialLoad, setInitialLoad] = React.useState(false)
  const [tableSort, setTableSort] = React.useState([])
  const [data, setData] = React.useState([])
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const [open, setOpen] = React.useState(false)

  const [searchFilter, setSearchFilter] = useSavedValue<string>({
    key: CUSTOMER_SEARCH_STORAGE_KEY,
    defaultValue: '""'
  })
  const [fieldValues, setFieldValues] = useSavedValue<SimpleValue[]>({
    key: CUSTOM_FIELDS_VALUE_STORAGE_KEY,
    defaultValue: '[]',
    loadTransform: val => val.slice(0, FIELD_LIMIT)
  })
  const includedCustom = React.useMemo(
    () => visible.filter(id => isUUID(id)).slice(0, FIELD_INCLUDE_LIMIT),
    [visible]
  )

  const showPagination = React.useMemo(() => {
    if (!pagination) {
      return false
    }

    const { per_page } = pagination
    return per_page < paginationTotal
  }, [pagination, paginationTotal])

  const canSearch = React.useMemo(
    () => searchFilter.length > MIN_CHAR_COUNT,
    [searchFilter]
  )

  const customFieldSearch: CustomFieldSearch = React.useMemo(
    () => ({
      customFields,
      fields: fieldValues.map(({ field_id }) => field_id),
      values: fieldValues,
      includedCustom
    }),
    [customFields, fieldValues, includedCustom]
  )

  const classes = useStyles()

  // Reset pagination state on filter change
  React.useEffect(() => {
    dispatch(resetPagination())
  }, [searchFilter, fieldValues, includedCustom, dispatch])

  const customerCustomFields: ColumnDef<WithMeta<LocalUser>, any>[] =
    React.useMemo(
      () =>
        customFields
          .filter(x => x.object_type === 'external-user')
          .map(field =>
            // @ts-ignore
            columnHelper.accessor(field.id, {
              header: () => <Header>{field.title}</Header>,
              cell: ({ row }) => <CustomField row={row} customField={field} />,
              enableSorting: false
            })
          ),
      [customFields]
    )

  const reloadTableData = React.useCallback(() => {
    dispatch(
      fetchPageOfUsers(filters, sort, customFieldSearch, data => {
        setData(data)
        updateUsers(data)
      })
    )
  }, [dispatch, updateUsers, filters, customFieldSearch, sort])

  const reloadAfterNew = React.useCallback(() => {
    reloadTableData()
  }, [reloadTableData])

  // Does filtering and sorting of users
  const preparedUsers = React.useMemo(() => {
    const filtered = applySearchFilter ? results : data

    const permFiltered = filterPermissions(
      hasPermission,
      filtered,
      true,
      currentUser
    )
    return [...permFiltered]
  }, [applySearchFilter, hasPermission, currentUser, data, results])

  const handleRowClicked = React.useCallback(
    row => {
      history.push(`${Routes.CUSTOMERS}/${row.original.id}`)
    },
    [history]
  )

  const handleClearClick = React.useCallback(() => {
    dispatch(resetSelection())
  }, [dispatch])

  const onDeleteClick = React.useCallback(() => {
    const action = async () => {
      const deleteDate = new Date().toISOString()
      const toDelete = selectedUsers.map(
        (u): UserPatch => ({
          id: u,
          deleted_at: deleteDate
        })
      ) as Array<UserPatch>
      try {
        await deleteUsers(toDelete)
        dispatch(resetSelection())
        reloadAfterNew()
        dispatch(AddSuccessAlert('Contacts deleted'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    }

    setTitle('ARE YOU SURE?')
    setSubtext('')
    setAction(action)
    setReason(`Are you sure you want to delete the selected contacts?`)
    setV2(true)
  }, [
    setTitle,
    setAction,
    setReason,
    setSubtext,
    setV2,
    dispatch,
    reloadAfterNew,
    selectedUsers
  ])

  React.useEffect(() => {
    if (!statusesLoaded && !loading) {
      dispatch(fetchUserStatus())
    }
  }, [dispatch, statusesLoaded, loading])

  // Handles search results
  React.useEffect(() => {
    if (canSearch) {
      dispatch(resetSelection())
      updateUsers(results)
      setApplySearchFilter(true)
      setLoadingPages(false)
    }
  }, [dispatch, updateUsers, results, canSearch])

  // Trigger debounce search
  React.useEffect(() => {
    if (canSearch) {
      setLoadingPages(canSearch)
      handleSearch(searchFilter, null, filters, sort, customFieldSearch)
    } else {
      setApplySearchFilter(false)
    }
  }, [canSearch, handleSearch, searchFilter, filters, sort, customFieldSearch])

  React.useEffect(() => {
    return () => {
      dispatch(resetSelection())
    }
  }, [dispatch])

  // Contact page specific columns
  const columns = React.useMemo(() => {
    const avatarColumn = columnHelper.accessor('name', {
      header: () => <Header>Contacts</Header>,
      cell: ({ row }) => (
        <SelectableClient
          selectable={hasAdmin}
          client={row.original}
          observerId={TABLE_ID}
          onClick={() => {
            handleRowClicked(row)
          }}
        />
      )
    })

    const statusColumn = isMedium
      ? null
      : columnHelper.accessor('status_id', {
          header: () => <Header Icon={TickCircle}>Status</Header>,
          cell: ({ row }) => <ClientStatus client={row.original} />
        })

    const typeColumn = isMedium
      ? null
      : columnHelper.accessor('type_id', {
          header: () => <Header Icon={UserOctagon}>Type</Header>,
          cell: ({ row }) => <UserType client={row.original} />
        })

    const phoneColumn = isSmall
      ? null
      : columnHelper.accessor('phone', {
          header: () => <Header Icon={Call}>Phone</Header>,
          enableSorting: false,
          cell: ({ row }) => <Channel user={row.original} />
        })

    const tagColumn = isSmall
      ? null
      : columnHelper.accessor('tags', {
          header: () => <Header Icon={Tag}>Tags</Header>,
          enableSorting: false,
          cell: ({ row }) => <TagCell tags={row.original.tags} />
        })

    const emailColumn = isSmall
      ? null
      : columnHelper.accessor('email', {
          header: () => <Header Icon={Sms}>Email</Header>,
          enableSorting: false,
          cell: ({ row }) => <Channel user={row.original} isEmail />
        })

    const taskColumn = isMedium
      ? null
      : columnHelper.accessor('tasks', {
          header: () => <Header Icon={ClipboardTick}>Tasks</Header>,
          enableSorting: false,
          sortingFn: taskSorter(),
          sortDescFirst: true,
          cell: ({ row }) => <ClientTasks client={row.original} />
        })

    const createdBy = isMedium
      ? null
      : columnHelper.accessor('created_by', {
          header: () => <Header Icon={UserTag}>Created by</Header>,
          enableSorting: false,
          cell: ({ row }) => <CreatedBy client={row.original} />
        })

    const createdColumn = columnHelper.accessor('created_at', {
      header: () => <Header Icon={Calendar1}>Created</Header>,
      cell: ({ row }) => <DateCell value={row.original.created_at} />
    })

    const updatedColumn = columnHelper.accessor('updated_at', {
      header: () => <Header Icon={Calendar2}>Updated</Header>,
      cell: ({ row }) => <DateCell value={row.original.updated_at} />
    })

    const allColumns = [
      avatarColumn,
      statusColumn,
      typeColumn,
      phoneColumn,
      emailColumn,
      taskColumn,
      createdBy,
      createdColumn,
      updatedColumn,
      tagColumn,
      ...customerCustomFields
    ].filter(Boolean)

    const visibleColumns = allColumns.filter(col =>
      // @ts-ignore
      visible.includes(col.accessorKey)
    )

    const sortedColumns = visibleColumns.sort((a, b) => {
      // @ts-ignore
      const aIndex = order.indexOf(a.accessorKey)
      // @ts-ignore
      const bIndex = order.indexOf(b.accessorKey)
      return aIndex - bIndex
    })

    return sortedColumns
  }, [
    isSmall,
    isMedium,
    hasAdmin,
    handleRowClicked,
    customerCustomFields,
    visible,
    order
  ])

  // Resets pagination state and fetches first page
  const updatePageSize = React.useCallback(
    async size => {
      dispatch(setPaginationSize(size))
      //loadTableData(size, 0)
    },
    [dispatch]
  )

  // Goes to another page, fetching if required
  const changePage = React.useCallback(
    (newPage: number) => {
      if (pagination) {
        dispatch(setPaginationPage(newPage))
      }
    },
    [pagination, dispatch]
  )

  React.useEffect(() => {
    if (currentUser && !Boolean(filterCurrentUser)) {
      dispatch(setCurrentUser(currentUser?.id))
    }
  }, [dispatch, currentUser, filterCurrentUser])

  React.useEffect(() => {
    if (tableSort.length === 0) {
      return
    }

    const result = tableSort.map(item => parseSort(item)).filter(Boolean)
    dispatch(setSort({ sort: result, apiTableSort: tableSort }))
  }, [dispatch, tableSort])

  React.useEffect(() => {
    if (!Boolean(filterCurrentUser)) return
    setLoadingPages(true)
    dispatch(
      fetchPageOfUsers(filters, sort, customFieldSearch, data => {
        setData(data)
        updateUsers(data)
        setLoadingPages(false)
        setInitialLoad(true)
      })
    )
  }, [
    dispatch,
    updateUsers,
    filterCurrentUser,
    filters,
    customFieldSearch,
    sort,
    pagination
  ])

  const initialSort = React.useMemo(() => [...tableApiSort], [tableApiSort])

  React.useEffect(() => {
    const hasSavedOrderSettings = localStorage.getItem(
      CUSTOMER_TABLE_ORDER_SETTINGS_KEY
    )
    const hasSavedVisibilitySettings = localStorage.getItem(
      CUSTOMER_TABLE_VISIBILITY_SETTINGS_KEY
    )
    if (hasSavedOrderSettings) {
      dispatch(setOrder(JSON.parse(hasSavedOrderSettings)))
    } else {
      dispatch(setOrder([...Object.keys(ALL_COLUMNS)]))
    }
    if (hasSavedVisibilitySettings) {
      dispatch(setVisible(JSON.parse(hasSavedVisibilitySettings)))
    } else {
      dispatch(setVisible([...Object.keys(ALL_COLUMNS)]))
    }
    //eslint-disable-next-line
  }, [])

  const settings = React.useMemo(
    () => (
      <Setting3
        size={theme.size.base}
        style={{
          cursor: 'pointer',
          marginRight: isSmall ? '1rem' : '0'
        }}
        color={theme.palette.primary.main}
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          setOpen(true)
        }}
      />
    ),
    [isSmall, theme.size.base, theme.palette.primary.main]
  )

  return (
    <>
      <AgentPage scroll>
        {!initialLoad ? (
          <Loading />
        ) : (
          <div className={classes.root}>
            <Table
              id={TABLE_ID}
              data={preparedUsers}
              columns={columns}
              filter={applySearchFilter ? '' : searchFilter}
              onRowClicked={handleRowClicked}
              message={searchLoading ? 'Searching contacts...' : 'There are no contacts'}
              loaded={!loadingPages}
              enableSort
              // Pagination
              onSetPageSize={updatePageSize}
              onPageChange={changePage}
              showPagination={showPagination}
              pageNum={pagination.page}
              numResults={canSearch ? results.length : paginationTotal}
              manualPagination={!canSearch}
              pagesize={pagination.per_page}
              useManualSort={true}
              setTableSort={setTableSort}
              disableSortRemove={true}
              initialSort={initialSort}
              // UI
              isSmall={isSmall}
              padBottom={isSmall}
              disableHeader={isSmall}
            >
              <Table.Header>
                <Table.TopRow
                  title={
                    <div className={classes.title}>
                      Contacts
                      {isSmall && paginationTotal && (
                        <small>
                          ({canSearch ? results.length : paginationTotal}
                          results)
                        </small>
                      )}
                    </div>
                  }
                />

                <Table.BottomRow
                  searchVal={searchFilter}
                  onSearch={filter =>
                    setSearchFilter(filter.trim().toLocaleLowerCase())
                  }
                  fullWidth={false}
                  isSmall={isSmall}
                  preChildren={
                    !isSmall && (
                      <FieldFilters
                        limit={FIELD_LIMIT}
                        values={fieldValues}
                        setValues={setFieldValues}
                      />
                    )
                  }
                  className={classes.bottomRow}
                >
                  {showSelector && (
                    <React.Fragment>
                      <div
                        className={classes.selectionOption}
                        onClick={handleClearClick}
                      >
                        <Eraser
                          variant='Linear'
                          size={theme.size.base}
                          color={theme.palette.primary.main}
                          style={{ marginRight: '0.25rem' }}
                        />

                        {isSmall
                          ? 'CLEAR'
                          : `CLEAR ${selectedUsers.length} SELECTED`}
                      </div>
                      <div
                        className={classes.selectionOption}
                        onClick={onDeleteClick}
                      >
                        <ProfileDelete
                          variant='Linear'
                          size={theme.size.base}
                          color={theme.palette.primary.main}
                          style={{ marginRight: '0.25rem' }}
                        />

                        {isSmall
                          ? 'DELETE'
                          : `DELETE ${selectedUsers.length} SELECTED`}
                      </div>
                    </React.Fragment>
                  )}

                  <Filter filter={filters}>
                    {isSmall && (
                      <>
                        <FieldFilters
                          limit={FIELD_LIMIT}
                          values={fieldValues}
                          setValues={setFieldValues}
                        />

                        {settings}
                      </>
                    )}
                  </Filter>

                  {!isSmall && settings}
                </Table.BottomRow>

                {fieldValues.length > 0 && (
                  <div className={classes.customSearchRow}>
                    <FieldPills
                      values={fieldValues}
                      setValues={setFieldValues}
                    />
                  </div>
                )}
              </Table.Header>
            </Table>
          </div>
        )}
      </AgentPage>

      {!isSmall && <ActionButton screen='user' cb={reloadAfterNew} />}

      {error && <ErrorToast error={error} />}

      {open && (
        <TableSettingsModal open={open} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

export default Customers
